import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';

interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  clientUserId: string;
  createdAt?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, firstName: string, lastName: string) => Promise<void>;
  updateProfile: (firstName: string, lastName: string) => Promise<void>;
  resendVerification: (email: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper function to decode JWT token
const decodeJWT = (token: string): any => {
  try {
    // JWT has three parts: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid JWT token format');
    }
    
    // Decode the payload (second part)
    const payload = parts[1];
    // Replace URL-safe base64 characters and add padding if needed
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    
    // Decode base64 and parse JSON
    const decoded = JSON.parse(atob(padded));
    return decoded;
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

// Configure axios defaults
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
axios.defaults.baseURL = API_BASE_URL;

// Add request interceptor to include auth token
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor to handle auth errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('auth_token'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check if user is logged in on app start
  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem('auth_token');
      console.log('Checking auth on page load, token exists:', !!storedToken);
      
      if (storedToken) {
        try {
          // Decode token to get id and email
          const decoded = decodeJWT(storedToken);
          console.log('Decoded JWT on page load:', decoded);
          
          if (decoded) {
            // Try different token structures
            const userId = decoded.user?.id || decoded.userId || decoded.id || decoded.sub;
            const email = decoded.user?.email || decoded.email;
            
            console.log('Extracted userId on page load:', userId, 'email:', email);
            
            if (userId && email) {
              // Set basic info from token (no user details fetch)
              const userData: User = {
                id: userId,
                email: email,
                firstName: decoded.user?.firstName || decoded.firstName || decoded.first_name || '',
                lastName: decoded.user?.lastName || decoded.lastName || decoded.last_name || '',
                clientUserId: decoded.user?.clientUserId || decoded.clientUserId || decoded.client_user_id || '',
                createdAt: decoded.createdAt || decoded.created_at || decoded.iat ? new Date(decoded.iat * 1000).toISOString() : undefined
              };
              setUser(userData);
              setToken(storedToken);
            } else {
              console.log('Missing userId or email, trying /auth/me fallback');
              // Fallback to API call if token decode fails or missing fields
              const response = await axios.get('/auth/me');
              setUser(response.data.user);
              setToken(storedToken);
            }
          } else {
            console.log('Token decode failed, trying /auth/me fallback');
            // Fallback to API call if token decode fails
            const response = await axios.get('/auth/me');
            setUser(response.data.user);
            setToken(storedToken);
          }
        } catch (error: any) {
          console.error('Error checking auth on page load:', error);
          localStorage.removeItem('auth_token');
          setToken(null);
          setUser(null);
        }
      } else {
        console.log('No token found, user not logged in');
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setError(null);
      setLoading(true);
      
      const response = await axios.post('/login', { email, password });
      console.log('Login response:', response.data);
      
      // Handle different response structures for token
      const token = response.data.token || response.data.data?.token || response.data.accessToken || response.data.access_token || response.data;
      
      if (!token) {
        throw new Error('No token received from server');
      }
      
      // Store token
      localStorage.setItem('auth_token', token);
      setToken(token);
      
      // Decode JWT token to get id and email

      console.log('Token:', token);
      const decoded = decodeJWT(token);
      console.log('Decoded JWT:', decoded);
      
      if (decoded) {
        const userId = decoded.user.id || decoded.id || decoded.sub;
        const email = decoded.user.email;
        
        console.log('Extracted userId:', userId, 'email:', email);
        
        if (userId && email) {
          // Set basic info from token (no user details fetch)
          const userData: User = {
            id: userId,
            email: email,
            firstName: decoded.user?.firstName || decoded.firstName || decoded.first_name || '',
            lastName: decoded.user?.lastName || decoded.lastName || decoded.last_name || '',
            clientUserId: decoded.user?.clientUserId || decoded.clientUserId || decoded.client_user_id || '',
            createdAt: decoded.createdAt || decoded.created_at || decoded.iat ? new Date(decoded.iat * 1000).toISOString() : undefined
          };
          setUser(userData);
        } else {
          // Fallback: if user data is included in response, use it
          const userData = response.data.user || response.data.data?.user;
          if (userData) {
            setUser(userData);
          }
        }
      } else {
        // Fallback: if user data is included in response, use it
        const userData = response.data.user || response.data.data?.user;
        if (userData) {
          setUser(userData);
        }
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Login failed';
      const requiresVerification = error.response?.data?.requiresVerification || false;
      setError(errorMessage);
      const err: any = new Error(errorMessage);
      err.requiresVerification = requiresVerification;
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const register = async (email: string, password: string, firstName: string, lastName: string) => {
    try {
      setError(null);
      setLoading(true);
      
      const response = await axios.post('/users', { 
        email, 
        password, 
        firstName, 
        lastName 
      });
      
      // With email verification, user is not automatically logged in
      // Just return success - no token or user data to store
      console.log('Registration response:', response.data);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Registration failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const resendVerification = async (email: string) => {
    try {
      setError(null);
      setLoading(true);
      
      await axios.post('/users/resend-verification', { email });
      // Backend returns generic message for security
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to resend verification email';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (firstName: string, lastName: string) => {
    try {
      if (!user) {
        throw new Error('User not logged in');
      }

      setError(null);
      setLoading(true);
      
      const response = await axios.put(`/users/${user.id}`, {
        firstName,
        lastName
      });
      
      // Update user state with new data
      const updatedUser: User = {
        ...user,
        firstName: response.data.firstName || response.data.first_name || firstName,
        lastName: response.data.lastName || response.data.last_name || lastName
      };
      
      setUser(updatedUser);
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to update profile';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('auth_token');
      setUser(null);
      setToken(null);
    }
  };

  const value = {
    user,
    token,
    login,
    register,
    updateProfile,
    resendVerification,
    logout,
    loading,
    error
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};